import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  CANONICAL_EVENTS,
  clearWhatsAppCompanyId,
  getProviderLabel,
  hasProviderConfigured,
  isValidE164Phone,
  persistWhatsAppCompanyId,
  sanitizeE164PhoneInput,
} from './whatsappTemplateShared';
import { useWhatsAppSettingsContext } from './WhatsAppSettingsContext';
import WhatsAppEventTemplateModal from './WhatsAppEventTemplateModal';
import api from '../../../../api/client';
import useCompanyData from '../../../../hooks/useCompanyData';
import NotificationPopup from '../../component/NotificationPopup';
import {
  Save,
  MessageCircle,
  CheckCircle2,
  ChevronRight,
  ChevronLeft,
  Pencil,
  Trash2,
  Shield,
  Phone,
  Bell,
  FileText,
  Eye,
  EyeOff,
} from 'lucide-react';
import { usePageLoading } from '../../../../contexts/LoadingContext';

/** Match EmailSettings / modern-theme (Poppins, cyan–magenta gradient) */
const FONT = { fontFamily: "'Poppins', sans-serif" };
const GRADIENT = 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)';
const INPUT_CLASS =
  'w-full px-4 py-[14px] rounded-xl bg-gray-50/50 border border-transparent focus:bg-white focus:border-blue-500/20 focus:ring-4 focus:ring-blue-500/5 transition-all text-gray-600 font-medium placeholder:text-gray-400';
const LABEL_CLASS = 'block text-[13px] font-bold text-gray-700 mb-2';
const CARD_CLASS =
  'bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl border border-gray-200/50 p-6 md:p-8';
const BTN_PRIMARY =
  'px-6 py-2.5 text-white rounded-xl font-bold text-sm hover:opacity-95 hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2';
const BTN_SECONDARY =
  'px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-xl font-bold text-sm hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm transition-all flex items-center gap-2';
const BTN_SAVE =
  'px-6 py-2.5 bg-[#6366f1] text-white rounded-xl font-bold text-sm hover:bg-[#4f46e5] hover:shadow-lg hover:shadow-indigo-500/20 transition-all disabled:opacity-50 flex items-center gap-2';

function PageLayout({ children }) {
  return (
    <div className="mx-auto w-full max-w-4xl" style={FONT}>
      {children}
    </div>
  );
}

const PROVIDERS = [
  {
    id: 'meta_cloud',
    label: 'Meta Cloud API',
    description: 'Direct WhatsApp Business Platform (Graph API)',
  },
  {
    id: 'twilio',
    label: 'Twilio',
    description: 'WhatsApp via Twilio Content API',
  },
  {
    id: '360dialog',
    label: '360dialog',
    description: 'BSP with Cloud API–compatible endpoints',
  },
  {
    id: 'interakt',
    label: 'Interakt',
    description: 'WhatsApp CRM and template API',
  },
];

const PROVIDER_FIELDS = {
  meta_cloud: [
    { key: 'access_token', label: 'Access Token', secret: true, required: true },
    { key: 'phone_number_id', label: 'Phone Number ID', required: true },
    { key: 'waba_id', label: 'WABA ID (optional)', required: false },
  ],
  twilio: [
    { key: 'account_sid', label: 'Account SID', required: true },
    { key: 'auth_token', label: 'Auth Token', secret: true, required: true },
    { key: 'from_number', label: 'WhatsApp From Number (E.164)', required: true },
  ],
  '360dialog': [
    { key: 'api_key', label: 'API Key (D360)', secret: true, required: true },
    { key: 'phone_number_id', label: 'Phone Number ID', required: true },
    {
      key: 'base_url',
      label: 'Base URL',
      placeholder: 'https://waba-v2.360dialog.io',
      required: false,
    },
  ],
  interakt: [{ key: 'api_key', label: 'API Key', secret: true, required: true }],
};

const STEPS = [
  { id: 1, labelKey: 'whatsappSettings.steps.chooseProvider', icon: Shield },
  { id: 2, labelKey: 'whatsappSettings.steps.credentials', icon: MessageCircle },
  { id: 3, labelKey: 'whatsappSettings.steps.eventsAndTemplates', icon: Bell },
];

function defaultEventToggles() {
  const out = {};
  CANONICAL_EVENTS.forEach((e) => {
    out[e.id] = true;
  });
  return out;
}

function hasSavedConfiguration(data) {
  return hasProviderConfigured(data);
}

function StepIndicator({ currentStep, t }) {
  return (
    <div className="flex items-center justify-between mb-8 px-2">
      {STEPS.map((step, index) => {
        const Icon = step.icon;
        const done = currentStep > step.id;
        const active = currentStep === step.id;
        return (
          <React.Fragment key={step.id}>
            <div className="flex flex-col items-center flex-1 min-w-0">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                  done
                    ? 'text-white border-transparent shadow-sm'
                    : active
                      ? 'border-[#0ea5e9] text-[#0ea5e9] bg-blue-50'
                      : 'border-gray-200 text-gray-400 bg-white'
                }`}
                style={done ? { background: GRADIENT } : undefined}
              >
                {done ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
              </div>
              <span
                className={`mt-2 text-xs text-center font-bold px-1 ${
                  active ? 'text-[#0284c7]' : 'text-gray-500'
                }`}
                style={FONT}
              >
                {t(step.labelKey)}
              </span>
            </div>
            {index < STEPS.length - 1 && (
              <div
                className={`h-0.5 flex-1 mx-2 mb-6 max-w-[80px] rounded-full ${
                  currentStep > step.id ? 'opacity-100' : 'bg-gray-200'
                }`}
                style={currentStep > step.id ? { background: GRADIENT } : undefined}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function WhatsAppSettings({ registerSaveBeforeTemplates }) {
  const { t } = useTranslation();
  const [, setSearchParams] = useSearchParams();
  const {
    updateDraft,
    saveConnection,
    companyId: contextCompanyId,
    serverSettings,
  } = useWhatsAppSettingsContext();
  const { companyId: companyIdFromHook, loading: companyLoading, error: companyError } =
    useCompanyData();
  const [actualCompanyId, setActualCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  usePageLoading(companyLoading || loading, {
    message: t('whatsappSettings.loading', 'Loading WhatsApp settings…'),
  });
  const [resolveError, setResolveError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [testing, setTesting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [viewMode, setViewMode] = useState('wizard');
  const [currentStep, setCurrentStep] = useState(1);
  const [savedConfig, setSavedConfig] = useState(null);

  const [enabled, setEnabled] = useState(true);
  const [activeProvider, setActiveProvider] = useState('meta_cloud');
  const [senderPhone, setSenderPhone] = useState('');
  const [requireOptIn, setRequireOptIn] = useState(true);
  const [eventToggles, setEventToggles] = useState({});
  const [providerCredentials, setProviderCredentials] = useState({});
  const [activeTemplateCount, setActiveTemplateCount] = useState(0);
  const [eventTemplateMap, setEventTemplateMap] = useState({});
  const [templateModalEventId, setTemplateModalEventId] = useState(null);
  const [disconnectConfirmOpen, setDisconnectConfirmOpen] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [showSecretFields, setShowSecretFields] = useState({});
  const [stepError, setStepError] = useState('');
  const [featureDisabled, setFeatureDisabled] = useState(false);

  const applyLoadedData = useCallback((data, openSummary = true) => {
    setFeatureDisabled(Boolean(data.feature_disabled));
    const provider = data.active_provider || 'meta_cloud';
    setEnabled(data.enabled !== false);
    setActiveProvider(provider);
    setSenderPhone(sanitizeE164PhoneInput(data.sender_phone_e164 || ''));
    setRequireOptIn(data.require_whatsapp_opt_in !== false);
    setEventToggles({ ...defaultEventToggles(), ...(data.event_toggles || {}) });
    setProviderCredentials(data.providers || {});
    setSavedConfig(data);
    if (openSummary && hasSavedConfiguration(data)) {
      setViewMode('summary');
    } else {
      setViewMode('wizard');
      setCurrentStep(1);
    }
  }, []);

  const resolveCompanyId = useCallback(async () => {
    if (companyIdFromHook) {
      setActualCompanyId(companyIdFromHook);
      persistWhatsAppCompanyId(companyIdFromHook);
      return companyIdFromHook;
    }
    try {
      const res = await api.get('/companies/');
      const companies = res.data?.results || res.data || [];
      if (Array.isArray(companies) && companies.length > 0) {
        const id = companies[0].id;
        setActualCompanyId(id);
        persistWhatsAppCompanyId(id);
        return id;
      }
      const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.company_id) {
          persistWhatsAppCompanyId(payload.company_id);
          setActualCompanyId(payload.company_id);
          return payload.company_id;
        }
      }
    } catch (err) {
      console.error('Failed to resolve company id', err);
    }
    setResolveError(t('whatsappSettings.messages.noCompany'));
    return null;
  }, [companyIdFromHook, t]);

  const loadSettings = useCallback(
    async (companyId, openSummary = true) => {
      const id = companyId ?? actualCompanyId;
      if (!id) return null;
      setLoading(true);
      setResolveError(null);
      try {
        const { data } = await api.get(`/companies/${id}/whatsapp-settings/`);
        applyLoadedData(data, openSummary);
        return data;
      } catch (err) {
        setSnackbar({
          open: true,
          message: err.response?.data?.error || t('whatsappSettings.messages.loadFailed'),
          severity: 'error',
        });
        return null;
      } finally {
        setLoading(false);
      }
    },
    [actualCompanyId, applyLoadedData, t]
  );

  useEffect(() => {
    if (companyLoading) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const id = companyIdFromHook || (await resolveCompanyId());
      if (cancelled || !id) {
        if (!cancelled) setLoading(false);
        return;
      }
      setActualCompanyId(id);
      persistWhatsAppCompanyId(id);
      await loadSettings(id, true);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyIdFromHook, companyLoading]);

  const pushDraftToContext = useCallback(() => {
    updateDraft({
      enabled: true,
      active_provider: activeProvider,
      sender_phone_e164: senderPhone,
      require_whatsapp_opt_in: requireOptIn,
      event_toggles: eventToggles,
      providers: providerCredentials,
    });
  }, [
    activeProvider,
    senderPhone,
    requireOptIn,
    eventToggles,
    providerCredentials,
    updateDraft,
  ]);

  useEffect(() => {
    pushDraftToContext();
  }, [pushDraftToContext]);

  const handleProviderChange = (pid) => {
    setActiveProvider(pid);
    setShowSecretFields({});
    setStepError('');
  };

  const handleSenderPhoneChange = (raw) => {
    setSenderPhone(sanitizeE164PhoneInput(raw));
    setStepError('');
  };

  const toggleSecretVisibility = (fieldKey) => {
    setShowSecretFields((prev) => ({ ...prev, [fieldKey]: !prev[fieldKey] }));
  };

  const goToTemplatesTab = () => {
    setSearchParams({ tab: 'templates' }, { replace: true });
  };

  const loadEventTemplateMap = useCallback(
    async (providerOverride) => {
      const cid = actualCompanyId || contextCompanyId;
      const pid = providerOverride || activeProvider;
      if (!cid || !pid) return;
      try {
        const entries = await Promise.all(
          CANONICAL_EVENTS.map(async (ev) => {
            const { data } = await api.get('/whatsapp-templates/by_event/', {
              params: { event_type: ev.id, provider: pid, company_id: cid },
            });
            const list = Array.isArray(data) ? data : [];
            return [ev.id, { templates: list, active: list.find((t) => t.is_active) || null }];
          })
        );
        const map = Object.fromEntries(entries);
        setEventTemplateMap(map);
        setActiveTemplateCount(
          Object.values(map).filter((entry) => entry.active).length
        );
      } catch {
        setEventTemplateMap({});
        setActiveTemplateCount(0);
      }
    },
    [actualCompanyId, contextCompanyId, activeProvider]
  );

  const loadActiveTemplateCount = useCallback(
    async (companyId, provider) => {
      if (!companyId || !provider) {
        setActiveTemplateCount(0);
        return;
      }
      await loadEventTemplateMap(provider);
    },
    [loadEventTemplateMap]
  );

  useEffect(() => {
    const pid = savedConfig?.active_provider || activeProvider;
    if (actualCompanyId && pid && hasSavedConfiguration(savedConfig || {})) {
      loadActiveTemplateCount(actualCompanyId, pid);
    }
  }, [actualCompanyId, savedConfig, activeProvider, loadActiveTemplateCount]);

  useEffect(() => {
    if (currentStep === 3 && (actualCompanyId || contextCompanyId) && activeProvider) {
      loadEventTemplateMap();
    }
  }, [currentStep, actualCompanyId, contextCompanyId, activeProvider, loadEventTemplateMap]);

  const handleEventToggleChange = (eventId, checked) => {
    setEventToggles((prev) => ({ ...prev, [eventId]: checked }));
    if (checked && !eventTemplateMap[eventId]?.active) {
      setTemplateModalEventId(eventId);
    }
  };

  const openTemplateModal = (eventId) => {
    setTemplateModalEventId(eventId);
  };

  const templateModalEvent = CANONICAL_EVENTS.find((e) => e.id === templateModalEventId);

  const openWizardAtStep = (step) => {
    if (savedConfig) {
      applyLoadedData(savedConfig, false);
    }
    setViewMode('wizard');
    setCurrentStep(step);
    setStepError('');
  };

  const setCredential = (key, value) => {
    setProviderCredentials((prev) => ({
      ...prev,
      [activeProvider]: { ...(prev[activeProvider] || {}), [key]: value },
    }));
    setStepError('');
  };

  const buildPayload = useCallback(() => {
    const creds = { ...(providerCredentials[activeProvider] || {}) };
    return {
      enabled: true,
      active_provider: activeProvider,
      sender_phone_e164: senderPhone,
      require_whatsapp_opt_in: requireOptIn,
      event_toggles: eventToggles,
      providers: { [activeProvider]: creds },
    };
  }, [activeProvider, providerCredentials, senderPhone, requireOptIn, eventToggles]);

  const validateStep = (step) => {
    if (step === 1) {
      if (!activeProvider) {
        setStepError(t('whatsappSettings.errors.selectProvider'));
        return false;
      }
    }
    if (step === 2) {
      const fields = PROVIDER_FIELDS[activeProvider] || [];
      const block = providerCredentials[activeProvider] || {};
      for (const f of fields) {
        if (!f.required) continue;
        const configured = block[`${f.key}_configured`];
        const value = (block[f.key] || '').trim();
        if (!configured && !value) {
          setStepError(t('whatsappSettings.errors.fieldRequired', { field: f.label }));
          return false;
        }
      }
      if (activeProvider === 'interakt') {
        const phone = senderPhone.trim();
        if (!phone) {
          setStepError(t('whatsappSettings.errors.senderPhoneRequired'));
          return false;
        }
        if (!isValidE164Phone(phone)) {
          setStepError(t('whatsappSettings.errors.senderPhoneInvalid'));
          return false;
        }
      }
    }
    setStepError('');
    return true;
  };

  useEffect(() => {
    if (!registerSaveBeforeTemplates) return undefined;
    registerSaveBeforeTemplates(async () => {
      const draftCheck = {
        active_provider: activeProvider,
        providers: providerCredentials,
        sender_phone_e164: senderPhone,
      };
      if (!hasProviderConfigured(draftCheck)) {
        return { ok: true, skipped: true };
      }
      if (!validateStep(2)) {
        return {
          ok: false,
          error: stepError || t('whatsappSettings.errors.fieldRequired', { field: 'credentials' }),
        };
      }
      return saveConnection(buildPayload());
    });
    return () => registerSaveBeforeTemplates(null);
  }, [
    registerSaveBeforeTemplates,
    buildPayload,
    saveConnection,
    stepError,
    t,
    activeProvider,
    providerCredentials,
    senderPhone,
    requireOptIn,
    eventToggles,
  ]);

  const handleSave = async () => {
    const companyId = actualCompanyId || contextCompanyId;
    if (!companyId) return false;
    if (!validateStep(2)) return false;
    setSaving(true);
    try {
      const result = await saveConnection(buildPayload());
      if (!result.ok) {
        setSnackbar({ open: true, message: result.error, severity: 'error' });
        return false;
      }
      persistWhatsAppCompanyId(companyId);
      const data = await loadSettings(companyId, true);
      await loadEventTemplateMap(activeProvider);
      setSnackbar({
        open: true,
        message: t('whatsappSettings.messages.saved'),
        severity: 'success',
      });
      setViewMode('summary');
      setCurrentStep(1);
      return !!data;
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || t('whatsappSettings.messages.saveFailed'),
        severity: 'error',
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const openDisconnectConfirm = () => {
    setDisconnectConfirmOpen(true);
  };

  const handleDisconnect = async () => {
    if (!actualCompanyId) return;
    setDisconnectConfirmOpen(false);
    setSaving(true);
    try {
      await api.put(`/companies/${actualCompanyId}/whatsapp-settings/`, {
        enabled: false,
        active_provider: null,
        providers: {},
        sender_phone_e164: '',
      });
      clearWhatsAppCompanyId();
      window.dispatchEvent(new CustomEvent('whatsapp-settings-updated'));
      await loadSettings(actualCompanyId, false);
      setViewMode('wizard');
      setCurrentStep(1);
      setEnabled(true);
      setSnackbar({
        open: true,
        message: t('whatsappSettings.messages.disconnected'),
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || t('whatsappSettings.messages.saveFailed'),
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleValidate = async () => {
    if (!actualCompanyId || !validateStep(2)) return;
    setValidating(true);
    try {
      await api.put(`/companies/${actualCompanyId}/whatsapp-settings/`, buildPayload());
      persistWhatsAppCompanyId(actualCompanyId);
      window.dispatchEvent(new CustomEvent('whatsapp-settings-updated'));
      const { data } = await api.post(
        `/companies/${actualCompanyId}/whatsapp-settings/validate/`
      );
      setSnackbar({
        open: true,
        message:
          data.message ||
          (data.ok
            ? t('whatsappSettings.messages.validateOk')
            : t('whatsappSettings.messages.validateFailed')),
        severity: data.ok ? 'success' : 'error',
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || t('whatsappSettings.messages.validateFailed'),
        severity: 'error',
      });
    } finally {
      setValidating(false);
    }
  };

  const extractApiError = (err, fallback) => {
    const data = err?.response?.data;
    if (!data) return fallback;
    const msg = data.error_message || data.error || data.detail || fallback;
    if (typeof msg === 'string' && msg.toLowerCase().includes('invalid token')) {
      return t('whatsappSettings.errors.interaktInvalidApiKey');
    }
    return msg;
  };

  const showUserFeedback = (message, severity = 'error') => {
    setStepError(message);
    setSnackbar({ open: true, message, severity });
  };

  const isSettingsDraftDirty = useMemo(() => {
    if (!serverSettings) return true;
    const payload = {
      enabled: true,
      active_provider: activeProvider,
      sender_phone_e164: senderPhone,
      require_whatsapp_opt_in: requireOptIn,
      event_toggles: eventToggles,
      providers: providerCredentials,
    };
    if (payload.active_provider !== serverSettings.active_provider) return true;
    if (
      sanitizeE164PhoneInput(payload.sender_phone_e164) !==
      sanitizeE164PhoneInput(serverSettings.sender_phone_e164 || '')
    ) {
      return true;
    }
    if (payload.require_whatsapp_opt_in !== serverSettings.require_whatsapp_opt_in) {
      return true;
    }
    const savedToggles = { ...defaultEventToggles(), ...(serverSettings.event_toggles || {}) };
    for (const ev of CANONICAL_EVENTS) {
      const draftOn = payload.event_toggles[ev.id] !== false;
      const savedOn = savedToggles[ev.id] !== false;
      if (draftOn !== savedOn) return true;
    }
    const pid = payload.active_provider;
    const draftBlock = payload.providers?.[pid] || {};
    const savedBlock = serverSettings.providers?.[pid] || {};
    const fields = PROVIDER_FIELDS[pid] || [];
    for (const f of fields) {
      const draftVal = (draftBlock[f.key] || '').trim();
      if (f.secret) {
        if (draftVal) return true;
      } else if (draftVal !== String(savedBlock[f.key] || '').trim()) {
        return true;
      }
    }
    return false;
  }, [
    serverSettings,
    activeProvider,
    senderPhone,
    requireOptIn,
    eventToggles,
    providerCredentials,
  ]);

  const canUseTestSend = useMemo(() => {
    if (!serverSettings || !hasSavedConfiguration(serverSettings)) return false;
    if (isSettingsDraftDirty) return false;
    if (
      !hasProviderConfigured({
        active_provider: serverSettings.active_provider,
        providers: serverSettings.providers,
        sender_phone_e164: serverSettings.sender_phone_e164,
      })
    ) {
      return false;
    }
    if (!eventTemplateMap.interview_invite?.active) return false;
    return true;
  }, [serverSettings, isSettingsDraftDirty, eventTemplateMap]);

  const handleTestSend = async () => {
    const companyId = actualCompanyId || contextCompanyId;
    if (!canUseTestSend) {
      if (isSettingsDraftDirty) {
        showUserFeedback(t('whatsappSettings.errors.testSaveFirst'));
      } else if (!eventTemplateMap.interview_invite?.active) {
        showUserFeedback(t('whatsappSettings.errors.testTemplateRequired'));
      } else {
        showUserFeedback(t('whatsappSettings.errors.testCredentialsRequired'));
      }
      return;
    }
    const phone = testPhone.trim();
    if (!companyId) {
      showUserFeedback(t('whatsappSettings.messages.noCompany'));
      return;
    }
    if (!phone) {
      showUserFeedback(t('whatsappSettings.errors.testPhoneRequired'));
      return;
    }
    if (!isValidE164Phone(phone)) {
      showUserFeedback(t('whatsappSettings.errors.testPhoneInvalid'));
      return;
    }
    setStepError('');
    setTesting(true);
    try {
      const { data } = await api.post(
        `/companies/${companyId}/whatsapp-settings/test-send/`,
        { phone_e164: phone, event_type: 'interview_invite' }
      );
      const failMsg = data.error_message || t('whatsappSettings.messages.testFailed');
      setSnackbar({
        open: true,
        message: data.success
          ? t('whatsappSettings.messages.testSent')
          : typeof failMsg === 'string' && failMsg.toLowerCase().includes('invalid token')
            ? t('whatsappSettings.errors.interaktInvalidApiKey')
            : failMsg,
        severity: data.success ? 'success' : 'error',
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: extractApiError(err, t('whatsappSettings.messages.testFailed')),
        severity: 'error',
      });
    } finally {
      setTesting(false);
    }
  };

  const goNext = () => {
    if (!validateStep(currentStep)) return;
    setCurrentStep((s) => Math.min(3, s + 1));
  };

  const goBack = () => {
    setStepError('');
    setCurrentStep((s) => Math.max(1, s - 1));
  };

  const summaryEnabledEvents = useMemo(() => {
    if (!savedConfig?.event_toggles) return [];
    return CANONICAL_EVENTS.filter((ev) => savedConfig.event_toggles[ev.id] !== false);
  }, [savedConfig]);

  if (companyLoading || loading) {
    return null;
  }

  if (resolveError || companyError) {
    return (
      <PageLayout>
        <div className="p-8 text-center text-red-600 max-w-lg mx-auto font-medium">
          {resolveError || companyError?.response?.data?.error || t('whatsappSettings.messages.loadFailed')}
        </div>
      </PageLayout>
    );
  }

  if (!actualCompanyId) {
    return (
      <PageLayout>
        <div className="p-8 text-center text-gray-600 max-w-lg mx-auto font-medium">
          {t('whatsappSettings.messages.noCompany')}
        </div>
      </PageLayout>
    );
  }

  const fields = PROVIDER_FIELDS[activeProvider] || [];

  if (viewMode === 'summary' && savedConfig && hasSavedConfiguration(savedConfig)) {
    const pid = savedConfig.active_provider;
    const providerBlock = savedConfig.providers?.[pid] || {};
    const enabledEventCount = summaryEnabledEvents.length;
    const templatesIncomplete = activeTemplateCount < enabledEventCount;

    return (
      <PageLayout>
        {templatesIncomplete && (
          <div className="bg-amber-50/90 backdrop-blur-sm rounded-2xl border border-amber-200/80 p-4 mb-6 shadow-sm">
            <p className="font-bold text-amber-900 text-sm" style={FONT}>
              {t('whatsappSettings.summary.templatesIncompleteTitle')}
            </p>
            <p className="text-xs text-amber-800/90 font-medium mt-1" style={FONT}>
              {t('whatsappSettings.summary.templatesIncompleteHint')}
            </p>
            <button
              type="button"
              onClick={goToTemplatesTab}
              className={`${BTN_PRIMARY} mt-3 text-xs`}
              style={{ ...FONT, background: GRADIENT }}
            >
              {t('whatsappSettings.summary.configureTemplates')}
            </button>
          </div>
        )}

        <div className="bg-emerald-50/50 backdrop-blur-sm rounded-2xl border border-emerald-100/50 p-4 mb-6 shadow-sm flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
          <div style={FONT}>
            <p className="font-bold text-gray-900 text-sm">
              {t('whatsappSettings.summary.connected')}
            </p>
            <p className="text-xs text-gray-500 font-medium mt-1">
              {t('whatsappSettings.summary.oneProviderNote')}
            </p>
          </div>
        </div>

        <div className={`${CARD_CLASS} divide-y divide-gray-100`}>
          <div className="pb-5 flex justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-400 font-bold">
                {t('whatsappSettings.summary.provider')}
              </p>
              <p className="text-lg font-bold text-gray-900 mt-1" style={FONT}>
                {getProviderLabel(pid)}
              </p>
            </div>
            <span className="h-fit px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest bg-white border border-emerald-100 text-emerald-600 shadow-sm">
              {savedConfig.enabled
                ? t('whatsappSettings.summary.statusActive')
                : t('whatsappSettings.summary.statusInactive')}
            </span>
          </div>

          {savedConfig.sender_phone_e164 && (
            <div className="py-5 flex items-center gap-3">
              <Phone className="w-4 h-4 text-gray-400" />
              <div>
                <p className="text-xs text-gray-400 font-bold">{t('whatsappSettings.senderPhone')}</p>
                <p className="text-sm font-bold text-gray-800" style={FONT}>
                  {savedConfig.sender_phone_e164}
                </p>
              </div>
            </div>
          )}

          <div className="py-5">
            <p className="text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">
              {t('whatsappSettings.summary.credentials')}
            </p>
            <ul className="text-sm text-gray-700 space-y-1 font-medium" style={FONT}>
              {Object.entries(providerBlock)
                .filter(([k]) => !k.endsWith('_encrypted') && k !== 'password')
                .map(([k, v]) => (
                  <li key={k} className="flex gap-2">
                    <span className="text-gray-400 capitalize">{k.replace(/_/g, ' ')}:</span>
                    <span>
                      {k.endsWith('_configured') || k.includes('configured')
                        ? v
                          ? t('whatsappSettings.summary.configured')
                          : '—'
                        : String(v || '—')}
                    </span>
                  </li>
                ))}
            </ul>
          </div>

          <div className="py-5">
            <p className="text-xs uppercase tracking-widest text-gray-400 font-bold mb-2 flex items-center gap-2">
              <Bell className="w-3.5 h-3.5" />
              {t('whatsappSettings.eventToggles')} ({summaryEnabledEvents.length})
            </p>
            <div className="flex flex-wrap gap-2">
              {summaryEnabledEvents.map((ev) => {
                const tpl = eventTemplateMap[ev.id]?.active;
                return (
                  <button
                    key={ev.id}
                    type="button"
                    onClick={() => openTemplateModal(ev.id)}
                    className="text-xs px-2.5 py-1 rounded-lg bg-gray-50 border border-gray-100 text-gray-700 font-bold hover:bg-gray-100 text-left"
                    style={FONT}
                  >
                    {t(ev.labelKey)}
                    <span className={tpl ? 'text-gray-400 font-medium' : 'text-amber-700 font-medium'}>
                      {' · '}
                      {tpl?.name || t('whatsappSettings.summary.templateNotConfigured')}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="py-5">
            <p className="text-xs uppercase tracking-widest text-gray-400 font-bold mb-2 flex items-center gap-2">
              <FileText className="w-3.5 h-3.5" />
              {t('whatsappSettings.templates')}
            </p>
            <p className="text-sm text-gray-600 mb-3" style={FONT}>
              {t('whatsappSettings.summary.activeTemplatesCount', {
                active: activeTemplateCount,
                total: enabledEventCount,
              })}
            </p>
            <button type="button" onClick={goToTemplatesTab} className={BTN_SECONDARY} style={FONT}>
              <FileText className="w-4 h-4" />
              {t('whatsappSettings.summary.manageTemplates')}
            </button>
          </div>

          <div className="pt-5 text-sm text-gray-600 font-medium" style={FONT}>
            {savedConfig.require_whatsapp_opt_in !== false
              ? t('whatsappSettings.summary.optInRequired')
              : t('whatsappSettings.summary.optInOptional')}
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mt-6">
          <button
            type="button"
            onClick={() => openWizardAtStep(1)}
            className={`${BTN_SAVE}`}
            style={FONT}
          >
            <Pencil className="w-4 h-4" />
            {t('whatsappSettings.summary.edit')}
          </button>
          <button
            type="button"
            onClick={goToTemplatesTab}
            className={BTN_SECONDARY}
            style={FONT}
          >
            <FileText className="w-4 h-4" />
            {t('whatsappSettings.summary.manageTemplates')}
          </button>
          <button
            type="button"
            onClick={openDisconnectConfirm}
            disabled={saving}
            className={`${BTN_SECONDARY} border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50`}
            style={FONT}
          >
            <Trash2 className="w-4 h-4" />
            {t('whatsappSettings.summary.disconnect')}
          </button>
        </div>

        <p className="text-xs text-gray-400 mt-6 font-medium" style={FONT}>
          {t('whatsappSettings.webhookNote')}
        </p>

        <WhatsAppEventTemplateModal
          open={Boolean(templateModalEventId)}
          onClose={() => setTemplateModalEventId(null)}
          eventId={templateModalEventId}
          eventLabel={templateModalEvent ? t(templateModalEvent.labelKey) : ''}
          companyId={actualCompanyId || contextCompanyId}
          activeProvider={savedConfig?.active_provider || activeProvider}
          onChanged={() => loadEventTemplateMap(savedConfig?.active_provider)}
        />

        <NotificationPopup
          open={snackbar.open}
          message={snackbar.message}
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        />

        {disconnectConfirmOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={FONT}>
            <button
              type="button"
              className="absolute inset-0 bg-black/40"
              aria-label="Close discard WhatsApp dialog"
              onClick={() => setDisconnectConfirmOpen(false)}
            />
            <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
              <div
                className="px-5 py-4 border-b border-gray-100"
                style={{
                  background:
                    'linear-gradient(135deg, rgba(14,165,233,0.08) 0%, rgba(217,70,239,0.08) 100%)',
                }}
              >
                <h3 className="text-base font-bold text-gray-900">
                  {t('whatsappSettings.summary.disconnect')}
                </h3>
              </div>
              <div className="px-5 py-4">
                <p className="text-sm text-gray-700">{t('whatsappSettings.messages.disconnectConfirm')}</p>
              </div>
              <div className="px-5 pb-5 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setDisconnectConfirmOpen(false)}
                  className={BTN_SECONDARY}
                  style={FONT}
                >
                  {t('whatsappTemplates.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleDisconnect}
                  className={`${BTN_PRIMARY} bg-red-600 hover:bg-red-700`}
                  style={FONT}
                >
                  <Trash2 className="w-4 h-4" />
                  {t('whatsappSettings.summary.disconnect')}
                </button>
              </div>
            </div>
          </div>
        )}
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      {featureDisabled && (
        <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-100 text-red-800 text-sm font-medium">
          {t('whatsappSettings.messages.featureDisabled')}
        </div>
      )}

      <p
        className="text-xs text-gray-500 mb-4 bg-gray-50/80 border border-gray-100 rounded-xl px-4 py-3 font-medium"
        style={FONT}
      >
        {t('whatsappSettings.oneProviderNote')}
      </p>

      <p
        className="text-xs text-[#0284c7] mb-6 bg-blue-50/80 border border-blue-100 rounded-xl px-4 py-3 font-medium"
        style={FONT}
      >
        {t('whatsappSettings.wizardGuide')}
      </p>

      <StepIndicator currentStep={currentStep} t={t} />

      <div className={`${CARD_CLASS} min-h-[320px]`}>
        {stepError && (
          <div className="mb-4 p-3 rounded-xl bg-red-50 text-red-700 text-sm border border-red-100 font-medium flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />
            {stepError}
          </div>
        )}

        {currentStep === 1 && (
          <div className="space-y-4">
            <div className="flex items-center gap-4 pb-4 border-b border-gray-100 mb-2">
              <div className="w-12 h-12 bg-[#6366f1]/10 rounded-full flex items-center justify-center text-[#6366f1] shadow-sm ring-1 ring-[#6366f1]/20">
                <Shield size={24} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 tracking-tight" style={FONT}>
                  {t('whatsappSettings.steps.chooseProvider')}
                </h2>
                <p className="text-xs text-gray-400 font-semibold" style={FONT}>
                  {t('whatsappSettings.steps.chooseProviderHint')}
                </p>
              </div>
            </div>
            <div className="grid gap-3">
              {PROVIDERS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => handleProviderChange(p.id)}
                  className={`text-left p-4 rounded-xl border-2 transition-all ${
                    activeProvider === p.id
                      ? 'border-[#0ea5e9] bg-blue-50/50 ring-2 ring-[#0ea5e9]/20 shadow-sm'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50/50'
                  }`}
                  style={FONT}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-gray-900">{p.label}</span>
                    {activeProvider === p.id && (
                      <CheckCircle2 className="w-5 h-5 text-[#0ea5e9]" />
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-1 font-medium">{p.description}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <div className="space-y-4">
            <div className="flex items-center gap-4 pb-4 border-b border-gray-100 mb-2">
              <div className="w-12 h-12 bg-[#0ea5e9]/10 rounded-full flex items-center justify-center text-[#0ea5e9] shadow-sm ring-1 ring-[#0ea5e9]/20">
                <MessageCircle size={24} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 tracking-tight" style={FONT}>
                  {t('whatsappSettings.steps.credentials')} — {getProviderLabel(activeProvider)}
                </h2>
                <p className="text-xs text-gray-400 font-semibold" style={FONT}>
                  {t('whatsappSettings.steps.credentialsHint')}
                </p>
              </div>
            </div>
            {fields.map((f) => (
              <div key={f.key}>
                <label className={LABEL_CLASS} style={FONT}>
                  {f.label}
                  {f.required && <span className="text-red-500 ml-0.5">*</span>}
                </label>
                {f.secret ? (
                  <div className="relative">
                    <input
                      type={showSecretFields[f.key] ? 'text' : 'password'}
                      className={`${INPUT_CLASS} pr-12`}
                      placeholder={
                        providerCredentials[activeProvider]?.[`${f.key}_configured`]
                          ? t('whatsappSettings.configuredPlaceholder')
                          : f.placeholder || ''
                      }
                      value={providerCredentials[activeProvider]?.[f.key] || ''}
                      onChange={(e) => setCredential(f.key, e.target.value)}
                      autoComplete="off"
                    />
                    <button
                      type="button"
                      onClick={() => toggleSecretVisibility(f.key)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                      aria-label={
                        showSecretFields[f.key]
                          ? t('whatsappSettings.hideApiKey')
                          : t('whatsappSettings.showApiKey')
                      }
                    >
                      {showSecretFields[f.key] ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                ) : (
                  <input
                    type="text"
                    className={INPUT_CLASS}
                    placeholder={
                      providerCredentials[activeProvider]?.[`${f.key}_configured`]
                        ? t('whatsappSettings.configuredPlaceholder')
                        : f.placeholder || ''
                    }
                    value={providerCredentials[activeProvider]?.[f.key] || ''}
                    onChange={(e) => setCredential(f.key, e.target.value)}
                  />
                )}
              </div>
            ))}
            <div>
              <label className={LABEL_CLASS} style={FONT}>
                {activeProvider === 'interakt'
                  ? t('whatsappSettings.senderPhoneInterakt')
                  : t('whatsappSettings.senderPhone')}
                {activeProvider === 'interakt' && <span className="text-red-500 ml-0.5">*</span>}
              </label>
              <input
                type="tel"
                inputMode="tel"
                className={INPUT_CLASS}
                value={senderPhone}
                onChange={(e) => handleSenderPhoneChange(e.target.value)}
                placeholder="+919876543210"
                maxLength={15}
                required={activeProvider === 'interakt'}
              />
              {activeProvider === 'interakt' && (
                <p className="text-xs text-gray-400 mt-1.5 font-medium" style={FONT}>
                  {t('whatsappSettings.senderPhoneInteraktHint')}
                </p>
              )}
            </div>
            <label
              className="flex items-start gap-2 text-sm text-gray-600 p-4 rounded-xl bg-gray-50/80 border border-gray-100 font-medium cursor-pointer"
              style={FONT}
            >
              <input
                type="checkbox"
                className="mt-1 rounded border-gray-300 text-[#6366f1] focus:ring-[#6366f1]"
                checked={requireOptIn}
                onChange={(e) => setRequireOptIn(e.target.checked)}
              />
              {t('whatsappSettings.requireOptIn')}
            </label>
            <button
              type="button"
              onClick={handleValidate}
              disabled={validating}
              className="text-sm text-[#6366f1] font-bold hover:underline disabled:opacity-50"
              style={FONT}
            >
              {validating
                ? t('whatsappSettings.validating')
                : t('whatsappSettings.validateOptional')}
            </button>
          </div>
        )}

        {currentStep === 3 && (
          <div className="space-y-6">
            <div className="flex items-center gap-4 pb-4 border-b border-gray-100">
              <div className="w-12 h-12 bg-[#d946ef]/10 rounded-full flex items-center justify-center text-[#d946ef] shadow-sm ring-1 ring-[#d946ef]/20">
                <Bell size={24} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 tracking-tight" style={FONT}>
                  {t('whatsappSettings.eventToggles')}
                </h2>
              </div>
            </div>
            <p className="text-xs text-gray-500 font-medium mb-3" style={FONT}>
              {t('whatsappSettings.eventTemplatesHint')}
            </p>
            <div className="space-y-2">
              {CANONICAL_EVENTS.map((ev) => {
                const enabled = eventToggles[ev.id] !== false;
                const activeTpl = eventTemplateMap[ev.id]?.active;
                return (
                  <div
                    key={ev.id}
                    className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 rounded-xl border border-gray-100 bg-white"
                    style={FONT}
                  >
                    <label className="flex items-center gap-2 text-sm font-medium cursor-pointer flex-1 min-w-0">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-[#6366f1] focus:ring-[#6366f1] shrink-0"
                        checked={enabled}
                        onChange={(e) => handleEventToggleChange(ev.id, e.target.checked)}
                      />
                      <span className="truncate">{t(ev.labelKey)}</span>
                    </label>
                    {enabled && (
                      <button
                        type="button"
                        onClick={() => openTemplateModal(ev.id)}
                        className={`text-left sm:text-right text-xs font-bold px-3 py-2 rounded-xl border shrink-0 transition-all ${
                          activeTpl
                            ? 'border-emerald-200 bg-emerald-50/80 text-emerald-900 hover:bg-emerald-100'
                            : 'border-amber-200 bg-amber-50/90 text-amber-900 hover:bg-amber-100'
                        }`}
                      >
                        {activeTpl
                          ? activeTpl.name
                          : t('whatsappSettings.selectTemplate')}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="p-4 rounded-xl bg-indigo-50/80 border border-indigo-100">
              <p className="text-xs text-gray-600" style={FONT}>
                {t('whatsappSettings.templatesManagedInTab')}
              </p>
              <button
                type="button"
                onClick={goToTemplatesTab}
                className={`${BTN_SECONDARY} mt-2`}
                style={{ ...FONT, borderColor: '#c7d2fe', color: '#4338ca' }}
              >
                <FileText className="w-4 h-4" />
                {t('whatsappSettings.summary.manageTemplates')}
              </button>
            </div>

            <div
              className={`p-4 rounded-xl border border-dashed ${
                canUseTestSend
                  ? 'border-gray-200 bg-gray-50/80'
                  : 'border-gray-200 bg-gray-100/80 opacity-90'
              }`}
            >
              <label
                className={`${LABEL_CLASS} ${!canUseTestSend ? 'text-gray-400' : ''}`}
                style={FONT}
              >
                {t('whatsappSettings.testPhone')}
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="tel"
                  inputMode="tel"
                  className={`flex-1 ${INPUT_CLASS} bg-white disabled:cursor-not-allowed disabled:opacity-60`}
                  value={testPhone}
                  onChange={(e) => {
                    setTestPhone(sanitizeE164PhoneInput(e.target.value));
                    setStepError('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && canUseTestSend) {
                      e.preventDefault();
                      handleTestSend();
                    }
                  }}
                  placeholder="+919876543210"
                  maxLength={15}
                  disabled={!canUseTestSend || testing}
                />
                <button
                  type="button"
                  onClick={() => handleTestSend()}
                  disabled={!canUseTestSend || testing || saving}
                  className={`${BTN_PRIMARY} whitespace-nowrap min-w-[140px] disabled:opacity-50 disabled:cursor-not-allowed`}
                  style={{ ...FONT, background: GRADIENT }}
                >
                  {testing ? t('whatsappSettings.testing') : t('whatsappSettings.testSend')}
                </button>
              </div>
              {!canUseTestSend && (
                <p className="text-xs text-amber-800 mt-2 font-medium rounded-lg bg-amber-50 border border-amber-100 px-3 py-2" style={FONT}>
                  {isSettingsDraftDirty
                    ? t('whatsappSettings.testDisabledUnsaved')
                    : t('whatsappSettings.testDisabledIncomplete')}
                </p>
              )}
              {stepError && canUseTestSend && (
                <p
                  className="text-xs text-red-600 mt-2 font-medium rounded-lg bg-red-50 border border-red-100 px-3 py-2"
                  style={FONT}
                  role="alert"
                >
                  {stepError}
                </p>
              )}
              <p className="text-xs text-gray-400 mt-2 font-medium" style={FONT}>
                {t('whatsappSettings.testHint')}
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mt-6 gap-3">
        <div>
          {currentStep > 1 ? (
            <button type="button" onClick={goBack} className={BTN_SECONDARY} style={FONT}>
              <ChevronLeft className="w-4 h-4" />
              {t('whatsappSettings.back')}
            </button>
          ) : viewMode === 'wizard' && savedConfig && hasSavedConfiguration(savedConfig) ? (
            <button
              type="button"
              onClick={() => setViewMode('summary')}
              className="text-sm text-gray-500 hover:text-gray-900 font-bold"
              style={FONT}
            >
              {t('whatsappSettings.cancelEdit')}
            </button>
          ) : null}
        </div>

        <div className="flex gap-2">
          {currentStep < 3 ? (
            <button
              type="button"
              onClick={goNext}
              className={BTN_PRIMARY}
              style={{ ...FONT, background: GRADIENT }}
            >
              {t('whatsappSettings.next')}
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className={BTN_SAVE}
              style={FONT}
            >
              <Save className="w-4 h-4" />
              {saving ? t('whatsappSettings.saving') : t('whatsappSettings.save')}
            </button>
          )}
        </div>
      </div>

      {viewMode === 'wizard' && currentStep < 3 && (
        <div className="mt-4 text-center">
          <p className="text-sm text-gray-500 font-medium" style={FONT}>
            {currentStep === 1
              ? t('whatsappSettings.emptyStateStep1')
              : t('whatsappSettings.emptyStateStep2')}
          </p>
        </div>
      )}

      <WhatsAppEventTemplateModal
        open={Boolean(templateModalEventId)}
        onClose={() => setTemplateModalEventId(null)}
        eventId={templateModalEventId}
        eventLabel={templateModalEvent ? t(templateModalEvent.labelKey) : ''}
        companyId={actualCompanyId || contextCompanyId}
        activeProvider={activeProvider}
        onChanged={loadEventTemplateMap}
      />

      <NotificationPopup
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </PageLayout>
  );
}
